import { computed, defineComponent, onMounted, ref } from 'vue';
import { LocationQuery, onBeforeRouteUpdate, RouteLocationRaw, useRoute, useRouter } from 'vue-router';
import { DASHBOARD_SECTIONS_ROUTES } from '@/models/common/permissions.model';
import { storeToRefs } from 'pinia';
import { usePermissionsStore } from '@/store/permissions.store';
import { DASHBOARD_META_TITLES } from '@/models/common/meta-titles.model';
import MultiWhitelabel from '@/components/multi-whitelabel/multi-whitelabel';
import { useDocumentList } from '@/hooks/documents/document-list.composables';
import { usePayoutOrderList } from '@/hooks/payout-order/payout-order-list.composables';
import { useWhitelabelListStore } from '@/store/dictionary/whitelabel-list.store';
import { useMultiWhitelabelListStore } from '@/store/multi-whitelabel-slug-list/multi-whitelabel-slug-list.store';
import { usePayoutOrderListFilter } from '@/hooks/payout-order/payout-order-list-filter.composables';
import { useDocumentListFilter } from '@/hooks/documents/document-list-filter.composables';

type AvailableTabList = {
  name: DASHBOARD_SECTIONS_ROUTES;
  label: string;
  isAllowed: boolean;
}

export default defineComponent({
  name: 'DashboardPage',
  components: {
    MultiWhitelabel,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const isMultiWhitelabelListRestored = ref<boolean>(false);
    const whitelabelListStore = useWhitelabelListStore();
    const { getWhitelabelList } = whitelabelListStore;
    const { whitelabelList } = storeToRefs(whitelabelListStore);

    const { restoreMultiWhitelabelList } = useMultiWhitelabelListStore();
    const { documentsPermission, statisticsPermission, payoutOrderPermissions } = storeToRefs(usePermissionsStore());
    const { submitAndValidateForm: submitAndValidateDocumentListForm } = useDocumentListFilter();
    const { loadDocumentList } = useDocumentList();
    const { submitAndValidateForm: submitAndValidatePayoutOrderForm } = usePayoutOrderListFilter();
    const { loadPayoutOrderList } = usePayoutOrderList();
    const { submitAndValidateForm: submitAndValidatePayoutOrderListFilterForm } = usePayoutOrderListFilter();
    const { submitAndValidateForm: submitAndValidateDocumentListFilterForm } = useDocumentListFilter();

    const isGetDocumentListAllowed = computed<boolean>(() => documentsPermission.value.isGetDocumentListAllowed);
    const isStatisticsAllowed = computed<boolean>(() =>
      statisticsPermission.value.isGetStatisticsReportListAllowed ||
      statisticsPermission.value.isGetPlayersReportCountAllowed ||
      statisticsPermission.value.isGetTopWinsHistoryListAllowed);
    const isGetPayoutOrderListAllowed = computed<boolean>(() =>
      payoutOrderPermissions.value.isGetPayoutOrderListAllowed);

    const availableTabList = computed<AvailableTabList[]>(() => [
      {
        name: DASHBOARD_SECTIONS_ROUTES.PAYOUT_ORDER,
        label: DASHBOARD_META_TITLES.PAYOUT_ORDER_PAGE,
        isAllowed: isGetPayoutOrderListAllowed.value,
      },
      {
        name: DASHBOARD_SECTIONS_ROUTES.DOCUMENTS,
        label: DASHBOARD_META_TITLES.DOCUMENTS_PAGE,
        isAllowed: isGetDocumentListAllowed.value,
      },
      {
        name: DASHBOARD_SECTIONS_ROUTES.STATISTICS,
        label: DASHBOARD_META_TITLES.STATISTICS_PAGE,
        isAllowed: isStatisticsAllowed.value,
      },
    ].filter((tab) => tab.isAllowed));

    const defaultTab = computed<DASHBOARD_SECTIONS_ROUTES | undefined>(() => availableTabList.value[0]?.name);

    const isTabAvailable = (tabName: unknown): tabName is DASHBOARD_SECTIONS_ROUTES =>
      availableTabList.value.some(({ name }) => name === tabName);

    const activeTab = computed<DASHBOARD_SECTIONS_ROUTES>({
      get: () => (isTabAvailable(route.name) ? route.name : defaultTab.value ?? DASHBOARD_SECTIONS_ROUTES.PAYOUT_ORDER),
      set: (tabName) => {
        router.push({ name: tabName, query: route.query });
      },
    });

    const getAllowedTabLocation = (routeName: unknown, query: LocationQuery): RouteLocationRaw | undefined =>
      isTabAvailable(routeName) || !defaultTab.value
        ? undefined
        : { name: defaultTab.value, query };

    const redirectToAllowedTab = async (): Promise<void> => {
      const allowedTabLocation = getAllowedTabLocation(route.name, route.query);

      if (allowedTabLocation) {
        await router.replace(allowedTabLocation);
      }
    };

    const submitAndValidatePayoutOrderList = async (): Promise<void> => {
      const isSubmitAndValidatePayoutOrderForm: boolean = await submitAndValidatePayoutOrderForm();

      if (!isSubmitAndValidatePayoutOrderForm) {
        return;
      }

      await loadPayoutOrderList();
    };

    const submitAndValidateDocumentList = async (): Promise<void> => {
      const isSubmitAndValidateDocumentListForm: boolean = await submitAndValidateDocumentListForm();

      if (!isSubmitAndValidateDocumentListForm) {
        return;
      }

      await loadDocumentList();
    };

    const activeListActionMap: Record<string, () => Promise<void>> = {
      [DASHBOARD_SECTIONS_ROUTES.PAYOUT_ORDER]: submitAndValidatePayoutOrderList,
      [DASHBOARD_SECTIONS_ROUTES.DOCUMENTS]: submitAndValidateDocumentList,
    };

    const activeListActionRules: Record<string, () => Promise<boolean>> = {
      [DASHBOARD_SECTIONS_ROUTES.PAYOUT_ORDER]: submitAndValidatePayoutOrderListFilterForm,
      [DASHBOARD_SECTIONS_ROUTES.DOCUMENTS]: submitAndValidateDocumentListFilterForm,
    };

    const refreshCurrentActiveList = async (): Promise<void> => {
      const isValidActiveTabFilterForm = await activeListActionRules[activeTab.value]();

      if (!isValidActiveTabFilterForm) {
        return;
      }

      await activeListActionMap[activeTab.value]?.();
    };

    const initWhitelabelList = async (): Promise<void> => {
      try {
        await getWhitelabelList();
        restoreMultiWhitelabelList(whitelabelList.value);
      } finally {
        isMultiWhitelabelListRestored.value = true;
      }
    };

    onMounted(async () => {
      if (availableTabList.value.length) {
        await redirectToAllowedTab();
        await initWhitelabelList();
      }
    });

    onBeforeRouteUpdate((to) => getAllowedTabLocation(to.name, to.query) ?? true);

    return {
      activeTab,
      DASHBOARD_SECTIONS_ROUTES,
      isGetDocumentListAllowed,
      isStatisticsAllowed,
      isGetPayoutOrderListAllowed,
      availableTabList,
      refreshCurrentActiveList,
      isMultiWhitelabelListRestored,
    };
  },
});
